/**
 * Uygulama kabuğu: oturum, yönlendirme, üst çubuk.
 *
 * Her rota sunucunun kendi verisinden beslenir; istemcide katalog durumu
 * tutulmaz (sunucu tek doğru kaynaktır).
 */

import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type Capabilities, type Item, type Library } from './api.ts';
import { AddLibraryModal } from './components/AddLibraryModal.tsx';
import { BottomNav } from './components/BottomNav.tsx';
import { DiskBanner } from './components/DiskBanner.tsx';
import { ItemDeleteProvider } from './components/ItemDelete.tsx';
import { LayerBanner } from './components/LayerBanner.tsx';
import { MiniPlayer } from './components/MiniPlayer.tsx';
import { NowPlaying } from './components/NowPlaying.tsx';
import { OfflineBanner } from './components/OfflineBanner.tsx';
import { QueuePanel } from './components/QueuePanel.tsx';
import { SearchBox } from './components/SearchBox.tsx';
import { Sidebar } from './components/Sidebar.tsx';
import { Loading } from './components/States.tsx';
import { UpdateBanner } from './components/UpdateBanner.tsx';
import { isAudioItem } from './mediaKind.ts';
import {
  loadOfflineCatalog,
  loadOfflineItem,
  type OfflineCatalog,
  saveOfflineLibraries,
  saveOfflineLibraryPage,
} from './offlineCatalog.ts';
import { useRoute } from './route.ts';
import { AccountScreen } from './screens/AccountScreen.tsx';
import { AdminScreen } from './screens/AdminScreen.tsx';
import { DesignSystemScreen } from './screens/DesignSystemScreen.tsx';
import { HomeScreen } from './screens/HomeScreen.tsx';
import { ItemScreen } from './screens/ItemScreen.tsx';
import { LibrariesScreen } from './screens/LibrariesScreen.tsx';
import { LibraryScreen } from './screens/LibraryScreen.tsx';
import { LiveTvScreen } from './screens/LiveTvScreen.tsx';
import { LoginScreen } from './screens/LoginScreen.tsx';
import { MusicAlbumScreen, MusicArtistScreen } from './screens/MusicDetailScreens.tsx';
import { MusicScreen } from './screens/MusicScreen.tsx';
import { SearchScreen } from './screens/SearchScreen.tsx';
import { ShowsScreen } from './screens/ShowsScreen.tsx';
import { WatchlistScreen } from './screens/WatchlistScreen.tsx';
import { WatchScreen } from './screens/WatchScreen.tsx';
import { SessionProvider, useSession } from './session.tsx';
import { recordSwallowed } from './swallowed.ts';
import { useTvMode } from './tvMode.ts';
import { useAppUpdate } from './useAppUpdate.ts';
import { useAudioPlayer } from './useAudioPlayer.ts';
import { useDiskStatus } from './useDiskStatus.ts';
import { useLibraries } from './useLibraries.ts';
import { useOnlineStatus } from './useOnlineStatus.ts';
import { useSpatialNavigation } from './useSpatialNavigation.ts';

export function App(): ReactNode {
  return (
    <SessionProvider>
      <Shell />
    </SessionProvider>
  );
}

function Shell(): ReactNode {
  const { user, ready, logout } = useSession();
  const [route, navigate, back] = useRoute();
  const appUpdate = useAppUpdate();
  const [capabilities, setCapabilities] = useState<Capabilities | null>(null);
  const [capabilitiesError, setCapabilitiesError] = useState<string | null>(null);
  const [watching, setWatching] = useState<Item | null>(null);
  const [watchError, setWatchError] = useState<string | null>(null);
  /** "Baştan başlat" tıklaması bir sonraki WatchScreen kurulumuna taşınır. */
  const [watchStartOver, setWatchStartOver] = useState(false);
  const [showNowPlaying, setShowNowPlaying] = useState(false);
  const [showQueue, setShowQueue] = useState(false);
  /** Kalıcı silme sonrası ekranları yeniden kurup taze veri çektirir. */
  const [catalogVersion, setCatalogVersion] = useState(0);
  /*
   * TV / 10-foot modu: yalnız açık tercih, TV tarayıcısı veya kumanda tuşuyla
   * etkinleşir. Genişlik tek başına yeterli değildir.
   */
  const tvMode = useTvMode();
  useSpatialNavigation(tvMode.enabled);
  const [addLibModal, setAddLibModal] = useState<{
    isOpen: boolean;
    kind?: 'movie' | 'show' | 'mixed' | 'music';
    name?: string;
    path?: string;
  }>({ isOpen: false });

  /**
   * Katman erişilebilirliği periyodik yoklanır.
   *
   * Tek veri kaynağı burada tutulur ve hem uyarı bandına hem ekranlara
   * aktarılır; böylece aynı anda birden çok istek yapılmaz.
   */
  const { value: libraries, refresh: refreshLibraries, updatedAt: layersAt } = useLibraries();
  const online = useOnlineStatus();
  const [offlineCatalog, setOfflineCatalog] = useState<OfflineCatalog | null>(null);
  const prefetchedLibraries = useRef<string>('');
  const savedLibrariesKey = useRef<string>('');

  useEffect(() => {
    if (user === null) {
      setOfflineCatalog(null);
      return;
    }
    let cancelled = false;
    void loadOfflineCatalog(user.id).then((snapshot) => {
      if (!cancelled) setOfflineCatalog(snapshot);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (user === null || !online || libraries === null) return;
    const key = `${user.id}:${JSON.stringify(libraries)}`;
    if (savedLibrariesKey.current === key) return;
    savedLibrariesKey.current = key;
    void saveOfflineLibraries(user.id, libraries, user);
  }, [user, online, libraries]);

  /**
   * Kütüphane ilk sayfalarını önceden önbelleğe al. Kullanıcı o kütüphaneyi
   * hiç açmamış olsa bile uçak modunda içerik listesi görünür.
   */
  useEffect(() => {
    if (user === null || !online || libraries === null || libraries.length === 0) return;
    const key = `${user.id}:${libraries
      .map((library) => library.id)
      .sort()
      .join(',')}`;
    if (prefetchedLibraries.current === key) return;
    prefetchedLibraries.current = key;

    void (async () => {
      for (const library of libraries) {
        try {
          const page = await api.libraryItems(
            library.id,
            0,
            library.kind === 'music' ? 200 : 50,
            'title',
            'all',
          );
          await saveOfflineLibraryPage(user.id, {
            libraryId: library.id,
            offset: 0,
            sort: 'title',
            filter: 'all',
            items: page.items,
            total: page.total,
          });
        } catch (error) {
          recordSwallowed('App.offlineCatalog', error);
          // Soğuk/erişilemeyen kütüphane önbelleğe alınamaz; diğerleri devam eder.
        }
      }
    })();
  }, [user, online, libraries]);

  const effectiveLibraries = libraries ?? offlineCatalog?.libraries ?? null;

  /**
   * Kalıcı müzik oynatıcı (Tasarım V4, Öneri 7).
   *
   * `Shell` rota değişse de yaşadığı için `<audio>` ve mini oynatıcı
   * sökülmez; müzik kesintisiz devam eder. Kütüphane listesi sıra
   * oluşturmak için verilir.
   */
  const audioPlayer = useAudioPlayer(capabilities, effectiveLibraries);

  const wasOnlineRef = useRef(online);

  /**
   * Öğe bir müzik kütüphanesine mi ait?
   *
   * Müzik parçaları veritabanında `kind: 'movie'` olarak tutulur; tür ayrımı
   * kütüphanenin `kind`/adından gelir. Bilinmiyorsa dosya uzantısına düşülür.
   */
  const isMusicItem = (item: Item): boolean => {
    const library = libraries?.find((entry) => entry.id === item.libraryId);
    if (library !== undefined) {
      const lower = library.name.toLowerCase();
      return (
        library.kind === 'music' ||
        lower.includes('müzik') ||
        lower.includes('muzik') ||
        lower.includes('music')
      );
    }
    return isAudioItem(item);
  };

  /**
   * Disk kullanımı (ADR-014).
   *
   * Uzak diskin önbelleği sessizce yer kaplar; disk dolduğunda kullanıcı
   * ancak oynatma durduğunda fark eder. Bu yüzden uyarı her ekranda görünür.
   */
  const { value: diskStatus, updatedAt: diskAt } = useDiskStatus(user !== null);

  const openAddLibrary = (config?: {
    kind?: 'movie' | 'show' | 'mixed' | 'music';
    name?: string;
    path?: string;
  }) => {
    setAddLibModal({
      isOpen: true,
      kind: config?.kind ?? 'mixed',
      name: config?.name ?? '',
      path: config?.path ?? '',
    });
  };

  /**
   * Yetenekler **isteğe bağlı bir yardımcı istektir**.
   *
   * Ana sayfa, kütüphane, arama ve detay katalog API'lerinden beslenir; bu
   * istek başarısız olduğunda tüm uygulamayı spinner'da bırakmak yanlıştı.
   * Hata yalnız oynatma/hesap gibi yetenek gerektiren yüzeylerde gösterilir.
   */
  const loadCapabilities = useCallback(() => {
    setCapabilitiesError(null);
    // AbortController Chrome 66+; eski Tizen TV'lerde yok. Yokluğu tüm kabuğu
    // çökertmemeli: istek sinyal olmadan devam eder.
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer =
      controller === null ? undefined : window.setTimeout(() => controller.abort(), 8_000);
    void api
      .capabilities(controller?.signal)
      .then(setCapabilities)
      .catch((caught: unknown) => {
        setCapabilitiesError(
          caught instanceof Error
            ? caught.message
            : 'Sunucu yetenekleri okunamadı; oynatma için yeniden deneyin.',
        );
      })
      .finally(() => {
        if (timer !== undefined) window.clearTimeout(timer);
      });
  }, []);

  useEffect(() => {
    if (!user) return;
    loadCapabilities();
  }, [user, loadCapabilities]);

  // Bağlantı geri geldiğinde açık ekranı yeniden kurup taze veri çektir.
  useEffect(() => {
    if (online && !wasOnlineRef.current) setCatalogVersion((value) => value + 1);
    wasOnlineRef.current = online;
  }, [online]);

  const retryConnection = useCallback(() => {
    refreshLibraries();
    loadCapabilities();
    setCatalogVersion((value) => value + 1);
  }, [refreshLibraries, loadCapabilities]);

  /**
   * Doğrudan URL, geri/ileri tuşu veya kart tıklamasıyla 'watch' rotasına gelindiğinde
   * izlenecek öğe henüz hafızada yoksa veya farklıysa sunucudan otomatik çekilir.
   */
  useEffect(() => {
    if (!user || route.name !== 'watch') return;
    if (watching && watching.id === route.itemId) return;
    if (watchError !== null) return;

    let cancelled = false;
    setWatchError(null);
    void api
      .item(route.itemId)
      .then((item) => {
        if (!cancelled) setWatching(item);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setWatchError(err instanceof Error ? err.message : 'Öğe yüklenemedi.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user, route, watching, watchError]);

  const openWatch = async (itemId: string, startOver = false): Promise<void> => {
    try {
      setWatchError(null);
      setWatchStartOver(startOver);

      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        const cached = user === null ? null : await loadOfflineItem(user.id, itemId);
        if (cached !== null) setWatching(cached);
        setWatchError(
          'Çevrimdışı modda oynatma başlatılamaz. İnternet bağlantısı kurulunca tekrar deneyin.',
        );
        navigate({ name: 'watch', itemId });
        return;
      }

      const item = await api.item(itemId);
      /*
       * Müzik **rota değiştirmez**: global oynatıcıya verilir, mini oynatıcı
       * görünür ve kullanıcı kütüphanede gezinmeye devam eder (Öneri 7).
       * Video ise tam ekran `WatchScreen` rotasına gider; çalan müzik durur.
       */
      if (isMusicItem(item)) {
        audioPlayer.play(item);
        return;
      }
      audioPlayer.pause();
      setWatching(item);
      navigate({ name: 'watch', itemId });
    } catch (err: unknown) {
      setWatchError(err instanceof Error ? err.message : 'Öğe yüklenemedi.');
      navigate({ name: 'watch', itemId });
    }
  };

  useEffect(() => {
    if (route.name === 'watch') audioPlayer.pause();
    else setWatchStartOver(false);
  }, [route.name, audioPlayer.pause]);

  // Oynatıcı kapanınca açık sıra paneli boş bir yüzey bırakmasın.
  useEffect(() => {
    if (audioPlayer.current === null) setShowQueue(false);
  }, [audioPlayer.current]);

  const closeWatch = (): void => {
    setWatching(null);
    setWatchError(null);
    setWatchStartOver(false);
    back({ name: 'home' });
  };

  if (!ready) return <Loading label="Bağlanılıyor…" />;
  if (!user) return <LoginScreen />;

  return (
    <ItemDeleteProvider
      canDelete={user.role === 'admin'}
      onDeleted={(deleted) => {
        if (audioPlayer.current?.id === deleted.id) audioPlayer.close();
        refreshLibraries();
        setCatalogVersion((value) => value + 1);
        // Kullanıcıyı bulunduğu bağlamdan koparmadan listeyi tazele.
        if (route.name === 'library') navigate({ name: 'library', libraryId: route.libraryId });
        else if (route.name === 'search') navigate({ name: 'search', query: route.query });
        else navigate({ name: 'home' });
      }}
    >
      {/*
        Tek ve kalıcı `<audio>`: rota değişse de sökülmez. Mini oynatıcı ve
        "Şimdi Çalıyor" yüzeyi aynı öğeyi paylaşır (Tasarım V4, Öneri 7).
      */}
      {/* biome-ignore lint/a11y/useMediaCaption: müzik parçası; altyazı yok */}
      <audio ref={audioPlayer.attach} preload="metadata" className="audio-engine" />

      {!online && (
        <OfflineBanner onRetry={retryConnection} savedAt={offlineCatalog?.savedAt ?? null} />
      )}

      {route.name === 'watch' ? (
        <RouteView
          key={`watch-${catalogVersion}`}
          route={route}
          capabilities={capabilities}
          capabilitiesError={capabilitiesError}
          onRetryCapabilities={loadCapabilities}
          onBack={() => back({ name: 'home' })}
          libraries={effectiveLibraries}
          watching={watching}
          watchError={watchError}
          watchStartOver={watchStartOver}
          navigate={navigate}
          openWatch={openWatch}
          closeWatch={closeWatch}
          onEnqueue={audioPlayer.enqueue}
          onPlayTracks={(tracks) => {
            if (tracks[0]) audioPlayer.play(tracks[0], tracks);
          }}
          tvMode={tvMode}
        />
      ) : (
        <div className={`app-layout ${audioPlayer.current ? 'has-mini-player' : ''}`}>
          <Sidebar
            currentRoute={route}
            libraries={effectiveLibraries}
            user={user}
            onNavigate={navigate}
            onLogout={() => void logout()}
            onAddLibrary={() => openAddLibrary()}
          />

          <div className="app-main">
            <UpdateBanner update={appUpdate} />
            <header className="topbar">
              <div className="topbar__mobile-brand">
                <button
                  type="button"
                  className="topbar__brand"
                  onClick={() => navigate({ name: 'home' })}
                >
                  pbfork
                </button>
              </div>

              <SearchBox onOpenItem={(itemId) => navigate({ name: 'item', itemId })} />

              <div className="topbar__user">
                <button
                  type="button"
                  className="button button--ghost topbar__name"
                  onClick={() => navigate({ name: 'hesap' })}
                >
                  {user.displayName || user.username}
                </button>
                <button
                  type="button"
                  className="button button--ghost topbar__logout"
                  onClick={() => void logout()}
                >
                  Çıkış
                </button>
              </div>
            </header>

            <main className="content">
              <LayerBanner libraries={effectiveLibraries} updatedAt={layersAt} />
              <DiskBanner status={diskStatus} updatedAt={diskAt} />
              <RouteView
                key={`main-${catalogVersion}`}
                route={route}
                capabilities={capabilities}
                capabilitiesError={capabilitiesError}
                onRetryCapabilities={loadCapabilities}
                onBack={() => back({ name: 'home' })}
                libraries={effectiveLibraries}
                watching={watching}
                watchError={watchError}
                watchStartOver={watchStartOver}
                navigate={navigate}
                openWatch={openWatch}
                closeWatch={closeWatch}
                onEnqueue={audioPlayer.enqueue}
                onPlayTracks={(tracks) => {
                  if (tracks[0]) audioPlayer.play(tracks[0], tracks);
                }}
                tvMode={tvMode}
                onRefreshLibraries={refreshLibraries}
                onAddLibrary={openAddLibrary}
                canAddLibrary={user.role === 'admin'}
              />
            </main>
          </div>

          <BottomNav currentRoute={route} onNavigate={navigate} />

          {addLibModal.isOpen && (
            <AddLibraryModal
              isOpen={addLibModal.isOpen}
              initialKind={addLibModal.kind}
              initialName={addLibModal.name}
              initialPath={addLibModal.path}
              onClose={() => setAddLibModal({ isOpen: false })}
              onCreated={(newLib) => {
                refreshLibraries();
                navigate({ name: 'library', libraryId: newLib.id });
              }}
            />
          )}
        </div>
      )}

      {route.name !== 'watch' && (
        <>
          <MiniPlayer
            player={audioPlayer}
            onExpand={() => setShowNowPlaying(true)}
            onOpenQueue={() => setShowQueue(true)}
          />
          {showNowPlaying && (
            <NowPlaying
              player={audioPlayer}
              onClose={() => setShowNowPlaying(false)}
              onOpenQueue={() => setShowQueue(true)}
            />
          )}
          {showQueue && <QueuePanel player={audioPlayer} onClose={() => setShowQueue(false)} />}
        </>
      )}
    </ItemDeleteProvider>
  );
}

function RouteView({
  route,
  capabilities,
  capabilitiesError,
  onRetryCapabilities,
  onBack,
  libraries,
  watching,
  watchError,
  watchStartOver,
  navigate,
  openWatch,
  closeWatch,
  onEnqueue,
  onPlayTracks,
  tvMode,
  onRefreshLibraries,
  onAddLibrary,
  canAddLibrary,
}: {
  route: ReturnType<typeof useRoute>[0];
  capabilities: Capabilities | null;
  capabilitiesError: string | null;
  onRetryCapabilities: () => void;
  /** Uygulama içi geri; detaydan kaynak listeye döner. */
  onBack: () => void;
  libraries: Library[] | null;
  watching: Item | null;
  watchError: string | null;
  watchStartOver: boolean;
  navigate: ReturnType<typeof useRoute>[1];
  openWatch: (itemId: string, startOver?: boolean) => Promise<void>;
  closeWatch: () => void;
  onEnqueue: (item: Item) => void;
  onPlayTracks: (tracks: Item[]) => void;
  tvMode: ReturnType<typeof useTvMode>;
  onRefreshLibraries?: () => void;
  onAddLibrary?: (config?: {
    kind?: 'movie' | 'show' | 'mixed' | 'music';
    name?: string;
    path?: string;
  }) => void;
  /** "Kütüphane Ekle" eylemini yalnız yönetici rolü görebilir. */
  canAddLibrary?: boolean;
}): ReactNode {
  if (route.name === 'watch') {
    if (watchError) {
      return (
        <div
          className="state state--error"
          style={{ margin: '60px auto', maxWidth: 440, textAlign: 'center' }}
        >
          <p>{watchError}</p>
          <button type="button" className="button button--primary" onClick={closeWatch}>
            Ana Sayfaya Dön
          </button>
        </div>
      );
    }
    if (!watching || watching.id !== route.itemId) {
      return <Loading label="Oynatılacak öğe yükleniyor…" />;
    }
    if (!capabilities) {
      if (capabilitiesError === null) {
        return <Loading label="Sunucu yetenekleri okunuyor…" />;
      }
      return (
        <div
          className="state state--error"
          style={{ margin: '60px auto', maxWidth: 480, textAlign: 'center' }}
        >
          <strong>Oynatma başlatılamadı</strong>
          <p>{capabilitiesError}</p>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            Katalog ve diğer ekranlar çalışmaya devam eder; yalnız oynatma için sunucu yeteneklerine
            ihtiyaç var.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="button button--primary" onClick={onRetryCapabilities}>
              Tekrar dene
            </button>
            <button type="button" className="button button--ghost" onClick={closeWatch}>
              Geri
            </button>
          </div>
        </div>
      );
    }
    return (
      <WatchScreen
        key={route.itemId}
        item={watching}
        capabilities={capabilities}
        startOver={watchStartOver}
        onClose={closeWatch}
        /*
          "Sıradaki" geçişi kabukta yapılır: öğe kimliği değişince
          `WatchScreen` yeniden kurulur (`key`), oynatma sıfırlanır ve yeni
          öğenin izleme oturumu açılır.
        */
        onPlayNext={(itemId) => void openWatch(itemId)}
      />
    );
  }

  switch (route.name) {
    case 'hesap':
      return (
        <AccountScreen
          capabilities={capabilities}
          tvMode={tvMode}
          onBack={() => navigate({ name: 'home' })}
        />
      );
    case 'yonetim':
      return <AdminScreen />;
    case 'tasarim':
      return <DesignSystemScreen />;
    case 'home':
      return (
        <HomeScreen
          capabilities={capabilities}
          libraries={libraries}
          onOpenLibrary={(libraryId) => navigate({ name: 'library', libraryId })}
          onWatch={(itemId) => void openWatch(itemId)}
          onAddLibrary={canAddLibrary ? () => onAddLibrary?.() : undefined}
        />
      );
    case 'library':
      return (
        <LibraryScreen
          key={route.libraryId}
          libraryId={route.libraryId}
          library={libraries?.find((entry) => entry.id === route.libraryId) ?? null}
          onOpenItem={(itemId) => navigate({ name: 'item', itemId })}
          onWatch={(itemId) => void openWatch(itemId)}
          onEnqueue={onEnqueue}
          onOpenAlbum={(albumId) => navigate({ name: 'muzik-album', albumId })}
          onRefreshLibraries={onRefreshLibraries}
          canManage={canAddLibrary === true}
          onDeleted={() => {
            onRefreshLibraries?.();
            navigate({ name: 'home' });
          }}
        />
      );
    case 'item':
      return (
        <ItemScreen
          key={route.itemId}
          itemId={route.itemId}
          libraries={libraries}
          onWatch={(id) => void openWatch(id)}
          onBack={onBack}
        />
      );
    case 'search':
      return (
        <SearchScreen
          key={route.query}
          query={route.query}
          libraries={libraries}
          onWatch={(id) => void openWatch(id)}
          onOpenItem={(id) => navigate({ name: 'item', itemId: id })}
          onBack={onBack}
        />
      );
    case 'listem':
      return (
        <WatchlistScreen
          libraries={libraries}
          onWatch={(id) => void openWatch(id)}
          onOpenItem={(id) => navigate({ name: 'item', itemId: id })}
          onExplore={() => navigate({ name: 'home' })}
        />
      );
    case 'diziler':
      return (
        <ShowsScreen
          libraries={libraries}
          onOpenLibrary={(libraryId) => navigate({ name: 'library', libraryId })}
          onBack={() => navigate({ name: 'home' })}
          onAddLibrary={() =>
            onAddLibrary?.({ kind: 'show', name: 'Diziler', path: '/srv/media/diziler' })
          }
        />
      );
    case 'filmler': {
      const movieLib = libraries?.find((l) => l.kind === 'movie');
      if (movieLib) {
        return (
          <LibraryScreen
            key={movieLib.id}
            libraryId={movieLib.id}
            library={movieLib}
            onOpenItem={(itemId) => navigate({ name: 'item', itemId })}
            onWatch={(itemId) => void openWatch(itemId)}
            onRefreshLibraries={onRefreshLibraries}
            canManage={canAddLibrary === true}
          />
        );
      }
      return (
        <HomeScreen
          capabilities={capabilities}
          libraries={libraries}
          onOpenLibrary={(libraryId) => navigate({ name: 'library', libraryId })}
          onWatch={(itemId) => void openWatch(itemId)}
          onAddLibrary={canAddLibrary ? () => onAddLibrary?.() : undefined}
        />
      );
    }
    case 'kutuphaneler':
      return (
        <LibrariesScreen
          libraries={libraries}
          canAddLibrary={canAddLibrary ?? false}
          onOpenLibrary={(libraryId) => navigate({ name: 'library', libraryId })}
          onAddLibrary={() => onAddLibrary?.()}
          onOpenLiveTv={() => navigate({ name: 'canli-tv' })}
        />
      );
    case 'canli-tv':
      return <LiveTvScreen onBack={() => navigate({ name: 'home' })} />;
    case 'muzik':
      return (
        <MusicScreen
          libraries={libraries}
          onOpenLibrary={(libraryId) => navigate({ name: 'library', libraryId })}
          onOpenAlbum={(albumId) => navigate({ name: 'muzik-album', albumId })}
          onOpenArtist={(artistId) => navigate({ name: 'muzik-artist', artistId })}
          onBack={() => navigate({ name: 'home' })}
          onAddLibrary={() =>
            onAddLibrary?.({ kind: 'music', name: 'Müzikler', path: '/srv/media/muzikler' })
          }
        />
      );
    case 'muzik-album':
      return (
        <MusicAlbumScreen
          albumId={route.albumId}
          onBack={() => navigate({ name: 'muzik' })}
          onPlayTracks={onPlayTracks}
          onEnqueue={onEnqueue}
        />
      );
    case 'muzik-artist':
      return (
        <MusicArtistScreen
          artistId={route.artistId}
          onBack={() => navigate({ name: 'muzik' })}
          onOpenAlbum={(albumId) => navigate({ name: 'muzik-album', albumId })}
        />
      );
  }
}
